/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Collaboration
 * (C) Copyright IBM Corp. 2017, 2021
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

define('collaboration/api/sharing/ShareableItems',['jquery', '../../lib/@waca/core-client/js/core-client/ui/core/Class', '../../lib/@waca/baglass/js/baglass/utils/Utils', '../../lib/@waca/baglass/js/baglass/api/Url', '../../nls/StringResources', './GenerateImage'], function ($, Class, Utils, Url, StringResources, GenerateImage) {
	'use strict';

	var HTML_2_CANVAS_PROXY = 'v1/collaboration/proxy/html2canvas';
	var SHARE_CONTEXTUAL_ACTION_KEY = 'com.ibm.ca.collaboration.shareContextual';

	var SVG_NO_IMAGE = '<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">' + '<rect x="0" y="0" width="100%" height="100%" fill="#eaeaea"/>' + '<g transform="translate(-64, -64)">' + '<svg x="50%" y="50%" width="128" height="128">' + '<g fill="#c0bfc0">' + '<path d="M64,12L4,116h120L64,12z M64,20.004L117.074,112H10.926L64,20.004z"/>' + '<polygon points="60,56 60,64 62,84 66,84 68,64 68,56"/>' + '<circle cx="64" cy="92" r="4"/>' + '</g>' + '</svg>' + '</g>' + '</svg>';

	var ShareableItems = Class.extend( /** @lends ShareableItems */{

		/**
   * @desc Constructor for ShareableItems.
   * @constructs ShareableItems
   * @extends Class
   * @public
   * @param {object} options
   * @param {object} options.logger
   */
		init: function init(options) {
			this._url = new Url();
			this._proxy = options && options.html2canvasProxy || HTML_2_CANVAS_PROXY;
			this.setLogger(options);
		},

		setLogger: function setLogger(options) {
			if (options && options.glassContext) {
				this._logger = options.glassContext.getCoreSvc('.Logger');
			} else {
				this._logger = null;
			}
		},

		/**
   * Returns an object containing a link representing the current context.
   *
   * @instance
   * @param {object} glassContext
   * @param {object} target
   * @returns {Promise} resolved as an object that contains shareUrl and embedUrl
   */
		getLink: function getLink(glassContext, target) {
			return this._isValid(glassContext, target).then(function (isValid) {
				//if target exist, means share accessed from context menu so no need to validate
				if (!target && !isValid) {
					return '';
				}
				return this._getPublicContext(glassContext, target).then(function (publicContext) {
					return this._getActionController(glassContext, publicContext, target).then(function (actionController) {
						return this._url.getUrlMap(actionController, glassContext, publicContext).then(function (urlMap) {
							if (urlMap.link) {
								// special case: take the link as-is
								return { shareUrl: urlMap.link };
							}
							return {
								shareUrl: this._url.getUrl({ urlMap: urlMap }, glassContext),
								embedUrl: this._url.getUrl({ urlMap: urlMap, isEmbed: true }, glassContext)
							};
						}.bind(this));
					}.bind(this));
				}.bind(this));
			}.bind(this));
		},

		_isValid: function _isValid(glassContext, target) {
			return Promise.resolve(!!this._getAssetId(glassContext, target));
		},

		_getResourceToShare: function _getResourceToShare(glassContext, target) {
			return this._getPublicContext(glassContext, target).then(function (publicContext) {
				return this._getActionController(glassContext, publicContext, target).then(function (actionController) {
					return this._getShareableItems(actionController, publicContext).then(function (items) {
						var item = items[0];
						return {
							el: item.el && item.el.length ? item.el[0] : item.el,
							label: item.label,
							type: publicContext.urlMap.type
						};
					}.bind(this));
				}.bind(this));
			}.bind(this)).catch(function (err) {
				if (this._logger) {
					this._logger.error(err);
				}
				throw err;
			}.bind(this));
		},

		/**
   * Returns an object containing an image representing the current context.
   *
   * @instance
   * @param {object} glassContext
   * @returns {Promise} with image (as text).
   */
		getScreenshot: function getScreenshot(glassContext, target) {
			var _this = this;

			return this._getResourceToShare(glassContext, target).then(function (resource) {
				return _this._buildItem(resource.el, resource.label, resource.type);
			});
		},

		/**
   * Check whether it's possible to screenshot the current context.
   * 
   * @param {*} glassContext 
   * @param {*} target 
   * @returns {Promise} that resolves to true or false.
   */
		canCaptureImage: function canCaptureImage(glassContext, target) {

			var isPdfReport = function isPdfReport(resource) {
				var reportTypes = ['report', 'reportView', 'output'];
				if (reportTypes.indexOf(resource.type) > -1) {
					return $(resource.el).find('div#idPdfViewer').is(':visible');
				}
				return false;
			};

			return this._getResourceToShare(glassContext, target).then(function (resource) {
				if (isPdfReport(resource)) {
					return false;
				}

				// Add more checks here to detect resources that can't be captured.

				return true;
			});
		},

		/**
   * Gets share action controller
   *
   * @instance
   * @param {object} glassContext
   * @returns {Promise}
   */
		getActionController: function getActionController(glassContext, target) {
			return this._getPublicContext(glassContext, target).then(this._getActionController.bind(this, glassContext));
		},

		/**
   * Notifies share action controller after share slideout shows.
   * @param {object} options
   * @param {object} options.glassContext
   * @param {object} options.slideout
   * @instance
   * @returns {Promise}
   */
		enterShareState: function enterShareState(options) {
			return this.getActionController(options.glassContext).then(function (actionController) {
				if (actionController && actionController.enterShareState) {
					return actionController.enterShareState(options);
				}
			}.bind(this));
		},

		/**
   * Notifies share action controller after share slideout hides.
   * @param {object} options.glassContext
   * @param {object} options.slideout
   * @instance
   * @returns {Promise}
   */
		leaveShareState: function leaveShareState(options) {
			return this.getActionController(options.glassContext).then(function (actionController) {
				if (actionController && actionController.leaveShareState) {
					return actionController.leaveShareState(options);
				}
			}.bind(this));
		},

		_getActionController: function _getActionController(glassContext, context, target) {
			var type = context.urlMap.type;
			var selection = target && target.plugin && target.plugin.activeObject && target.plugin.activeObject.aSelectedContext;

			return Utils.getSharedResourceActionController(glassContext, type, selection);
		},

		_getPublicContext: function _getPublicContext(glassContext, target) {
			var isDefaultAction = !!target;
			target = target || {};
			return new Promise(function (resolve) {
				var publicContext = {
					urlMap: {
						objRef: this._getResourceObjRef(glassContext, target),
						type: this._getResourceType(glassContext, target)
					},
					mode: this._getMode(glassContext, target),
					isDefaultAction: isDefaultAction,
					target: target,
					glassContext: glassContext // required by getUrlMap
				};
				var mode = this._getResourceMode(glassContext, target);
				if (mode) {
					publicContext.urlMap.mode = mode;
				}
				resolve(publicContext);
			}.bind(this));
		},

		_getShareableItems: function _getShareableItems(actionController, publicContext) {
			return new Promise(function (resolve, reject) {
				if (actionController && typeof actionController.getShareableItems === 'function') {
					resolve(actionController.getShareableItems(publicContext));
				} else {
					reject(new Error(StringResources.get('error_not_implemented')));
				}
			});
		},

		_getGenerator: function _getGenerator() {
			return new GenerateImage(this._getGenerateImageOptions());
		},

		_getGenerateImageOptions: function _getGenerateImageOptions() {
			return {
				logger: this._logger,
				proxy: this._proxy,
				elementMap: {
					'mediaWidget': SVG_NO_IMAGE,
					'webpageWidget': SVG_NO_IMAGE
				},
				excludeClassList: ['bx--assistive-text']
			};
		},

		_buildItem: function _buildItem(el, label, resourceType) {
			var item = {};
			item.label = label;
			return new Promise(function (resolve, reject) {
				var generator = this._getGenerator();
				generator.generateImage(el, null, resourceType).then(function (image) {
					item.image = image;
					return item;
				}).then(resolve).catch(reject).finally(function () {
					if (generator) {
						generator.destroy();
					}
				});
			}.bind(this));
		},

		_getResourceType: function _getResourceType(glassContext, target) {
			var type = void 0;
			try {
				type = target.plugin.options[0].type;
			} catch (e) {
				type = glassContext.currentAppView.getType();
			}
			return type;
		},

		_getResourceObjRef: function _getResourceObjRef(glassContext, target) {
			var objRef = void 0;
			try {
				objRef = target.plugin.options[0].id;
			} catch (e) {
				objRef = this._getAssetId(glassContext, target);
			}
			return objRef;
		},

		_getAssetId: function _getAssetId(glassContext, target) {
			var content = glassContext.currentAppView.getContent();
			//Dashboard, Exploration, Storytelling & Data Modules updates content.objRef when an asset is saved
			//Report and Datasets updates content.application.storeID when it is saved
			//Notebooks update content.id
			var type = this._getResourceType(glassContext, target);
			var id = type === 'jupyterNotebook' ? content.id : content.objRef;
			return id || content.application && content.application.storeID;
		},

		_getResourceMode: function _getResourceMode(glassContext, target) {
			var mode = void 0;
			try {
				var obj = target.activeObject.aSelectedContext[0];
				if (obj.defaultScreenTip === 'story') {
					mode = 'story';
				}
			} catch (e) {
				var content = glassContext.currentAppView.getContent();
				mode = content.mode;
				if (mode === undefined && content.isStoryMode) {
					// When we open a story then reload the page
					mode = 'story';
				}
			}
			return mode;
		},

		_getMode: function _getMode(glassContext, target) {
			try {
				var isDynamic = target.itemId === SHARE_CONTEXTUAL_ACTION_KEY;
				return isDynamic ? Url.MODES.DYNAMIC : Url.MODES.CURRENT;
			} catch (e) {
				return Url.MODES.CURRENT;
			}
		}
	});

	return ShareableItems;
});
;

var _temp = function () {
	if (typeof __REACT_HOT_LOADER__ === 'undefined') {
		return;
	}
}();

;

